import type { DynamicThemeFix, Theme } from 'darkreader';
import type DarkReader from 'darkreader';
import type { PropsWithChildren } from 'react';
import {
  startTransition,
  useCallback,
  useState,
  createContext,
  useContext,
  useRef,
} from 'react';
import {
  useIsClient,
  useIsomorphicLayoutEffect,
  useMediaQuery,
  useReadLocalStorage,
} from 'usehooks-ts';

interface DarkModeContextType {
  isDarkMode: boolean;
  enableDarkMode: () => void;
  disableDarkMode: () => void;
  resetDarkMode: () => void;
}

export const DarkModeContext = createContext<DarkModeContextType>({
  isDarkMode: false,
  enableDarkMode: () =>
    console.error('DarkModeContext has not been initialized'),
  disableDarkMode: () =>
    console.error('DarkModeContext has not been initialized'),
  resetDarkMode: () =>
    console.error('DarkModeContext has not been initialized'),
});

export const useDarkMode = () => useContext(DarkModeContext);

export const useIsDarkMode = () => useDarkMode().isDarkMode;

const darkReaderThemeOptions: Partial<Theme> = {
  brightness: 115,
  contrast: 100,
  sepia: 0,
};

export const darkReaderIgnoreInlineStyleClass =
  'darkreader-ignore-inline-style';

export const darkReaderInvertClass = 'darkreader-invert';

const darkReaderFixes: DynamicThemeFix = {
  ignoreInlineStyle: [`.${darkReaderIgnoreInlineStyleClass}`],
  invert: [`.${darkReaderInvertClass}`],
} as DynamicThemeFix;

export const DarkModeProvider = ({ children }: PropsWithChildren) => {
  const darkReader = useRef<typeof DarkReader>();

  const isClient = useIsClient();

  const [status, setStatus] = useState<'idle' | 'loading' | 'ready'>('idle');
  const isReady = status === 'ready';

  // Lazy-load DarkReader when the client hydrates
  useIsomorphicLayoutEffect(() => {
    if (darkReader.current || status !== 'idle') return;

    startTransition(() => {
      setStatus('loading');

      import('darkreader').then(module => {
        startTransition(() => {
          darkReader.current = module;
          setStatus('ready');
        });
      });
    });
  }, []);

  // An unset (null) preference follows the system query; useMediaQuery keeps
  // that reactive, so auto mode stays in sync without a hand-rolled listener.
  const isDarkModePreferred = useReadLocalStorage<boolean>('darkmode');
  const isSystemDark = useMediaQuery('(prefers-color-scheme: dark)', {
    initializeWithValue: false,
  });

  // A transient override (true/false) takes precedence over the preference and
  // system query. Keeping it in state — not just toggling DarkReader — means a
  // temporary force is reflected in `isDarkMode` and not clobbered by a re-render.
  const [themeOverride, setThemeOverride] = useState<boolean | null>(null);

  // Gated on the client to keep the server and first client render stable.
  const isDarkMode = isClient
    ? (themeOverride ?? isDarkModePreferred ?? isSystemDark)
    : false;

  useIsomorphicLayoutEffect(() => {
    if (!isReady) return;

    if (isDarkMode) {
      darkReader.current?.enable(darkReaderThemeOptions, darkReaderFixes);
    } else {
      darkReader.current?.disable();
    }
  }, [isReady, isDarkMode]);

  const enableDarkMode = useCallback(() => setThemeOverride(true), []);
  const disableDarkMode = useCallback(() => setThemeOverride(false), []);
  const resetDarkMode = useCallback(() => setThemeOverride(null), []);

  return (
    <DarkModeContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values -- For some reason this causes a hydration mismatch when memoized
      value={{
        isDarkMode,
        enableDarkMode,
        disableDarkMode,
        resetDarkMode,
      }}
    >
      {children}
    </DarkModeContext.Provider>
  );
};

export default DarkModeProvider;
